import { useTranslation } from "react-i18next";
import { useTiers } from "~/api/swr/useTiers";
import { useDocuments } from "~/api/v2/useDocuments";
import { type DocumentSummary, type DocumentDetails } from "~/api/types";
import { type Option } from "~/molecules/ControlledCombobox";
import { getNoTierLabel, isUntiered } from "~/helpers/tier";

interface DocumentLinkingContext {
  key: string;
  title: string;
  selectedDocuments: Option[];
  options: Option[];
}

interface UseDocumentLinkingProps {
  document: DocumentDetails;
  selectedLinkedDocuments: Option[];
}

const getTitle = (document: DocumentSummary) =>
  document.latestPublishedRelease?.artifact.title ??
  document.latestRelease.artifact.title;

function getDocumentOptions(
  documents: DocumentSummary[],
  matchesTier: (doc: DocumentSummary) => boolean,
  existingDocs: Option[],
  currentDocId: number,
  currentCategoryId: number,
): Option[] {
  const byCategoryThenTitle = (a: DocumentSummary, b: DocumentSummary) => {
    const aSame = a.category.id === currentCategoryId;
    const bSame = b.category.id === currentCategoryId;
    if (aSame !== bSame) return aSame ? -1 : 1;
    if (a.category.id !== b.category.id) {
      return a.category.title.localeCompare(b.category.title);
    }
    return getTitle(a).localeCompare(getTitle(b));
  };

  return documents
    .filter(
      (doc) =>
        matchesTier(doc) &&
        !existingDocs.some((existing) => existing.value === `${doc.id}`) &&
        doc.id !== currentDocId,
    )
    .sort(byCategoryThenTitle)
    .map((doc) => ({
      value: `${doc.id}`,
      label: getTitle(doc),
      subtitle: doc.category.title,
    }));
}

export function useDocumentLinking({
  document,
  selectedLinkedDocuments,
}: UseDocumentLinkingProps) {
  const { t } = useTranslation();
  const { data: tiers } = useTiers();
  const { data: documents } = useDocuments();

  const isLoading = !document || !tiers || !documents;

  if (isLoading) {
    return { contexts: [], isLoading };
  }

  const tierIds = new Set(tiers.map((tier) => tier.id));
  const tierIdByDocId = new Map<number, number>();
  for (const doc of documents) {
    if (doc.tier !== null) {
      tierIdByDocId.set(doc.id, doc.tier.id);
    }
  }

  const tierIdOfLink = (link: Option) => tierIdByDocId.get(Number(link.value));

  const contexts: DocumentLinkingContext[] = [];

  for (const tier of tiers) {
    const selectedDocuments = selectedLinkedDocuments.filter(
      (link) => tierIdOfLink(link) === tier.id,
    );
    const options = getDocumentOptions(
      documents,
      (doc) => doc.tier?.id === tier.id,
      selectedDocuments,
      document.id,
      document.category.id,
    );

    if (options.length > 0 || selectedDocuments.length > 0) {
      contexts.push({
        key: `tier-${tier.id}`,
        title: tier.title,
        selectedDocuments,
        options,
      });
    }
  }

  const untieredSelected = selectedLinkedDocuments.filter((link) =>
    isUntiered(tierIdOfLink(link), tierIds),
  );
  const untieredOptions = getDocumentOptions(
    documents,
    (doc) => doc.tier === null,
    untieredSelected,
    document.id,
    document.category.id,
  );

  if (untieredSelected.length > 0 || untieredOptions.length > 0) {
    contexts.push({
      key: "untiered",
      title: getNoTierLabel(t),
      selectedDocuments: untieredSelected,
      options: untieredOptions,
    });
  }

  return { contexts, isLoading };
}
